import { Alert, Button, Card, Col, ConfigProvider, DatePicker, Divider, Form, InputNumber, Row, Segmented, Space, Statistic, Typography, Select } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AvenantAgencySelector from "./AvenantAgencySelector";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { applyAvenantAgenceCommission } from "../../../../services/avenantCommissionService";
import { rightsService } from "../../../../services/rightsService";
import MembrePersonnelCheck from "../../../../components/MembrePersonnelCheck";
import { calculerEcheance } from "../../../../variables/datesFunctions";

const { Text, Title } = Typography;
const { Option } = Select;

const Prorogation = (props) => {
  const [modalProrogation, setModalProrogation] = useState(props.open);
  const [loading, setLoading] = useState(false) 
  const [form] = Form.useForm();

  // États pour les données du contrat
  const [contratActuel, setContratActuel] = useState(null);
  const [dateEffetProrogation, setDateEffetProrogation] = useState(null);
  const [dureeProrogation, setDureeProrogation] = useState(6); // Par défaut 6 mois
  const [dateEcheanceProrogation, setDateEcheanceProrogation] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fraisAccessoires, setFraisAccessoires] = useState(0);
  const [joursEcoules, setJoursEcoules] = useState(0);
  // Échéance ORIGINALE (avant prorogation) : référence pour le défaut de date d'effet, le
  // plancher d'antidatage (E+1) et les jours écoulés. En édition de proposition, la
  // dateEcheance du contrat est déjà la NOUVELLE échéance → on récupère E séparément.
  const [echeanceOriginale, setEcheanceOriginale] = useState(null);

  // Éditions manuelles (prorogation) : frais accessoires (HCO079) et bonus/malus (HCO080, hors VT)
  const [accessoiresEdites, setAccessoiresEdites] = useState(null);
  const [bonusMalusEdite, setBonusMalusEdite] = useState(null);
  const [bonusMalusSens, setBonusMalusSens] = useState('BONUS');
  const canEditAccessoires = rightsService("HCO079");
  const isVt = (props?.contrat?.engins?.[0]?.categorie?.codeCategorie || "").startsWith("VT");
  const canEditBonus = rightsService("HCO080") && !isVt;
  const canMarkPersonnel = rightsService("HCO090") && !isVt;
  // Antidatage : avec le droit HCO081, on lève la fenêtre [échéance+1j ; échéance+30j] et
  // le blocage « plus de 30 jours » (cohérent avec le renouvellement et l'affaire nouvelle).
  const canAntidate = rightsService("HCO081");
  const BONUS_MAX = 35;
  // Membre du personnel : bonus pris au max personnel (séparé), éditable à la baisse.
  const [membrePersonnel, setMembrePersonnel] = useState(false);
  const [maxPersonnel, setMaxPersonnel] = useState(50);
  const effectiveBonusMax = membrePersonnel ? maxPersonnel : BONUS_MAX;
  const bonusMalusAbs = Math.abs(Number(bonusMalusEdite) || 0);
  const applyBonusSens = (sens, magnitude) => {
    setBonusMalusSens(sens);
    let a = Math.abs(Number(magnitude) || 0);
    if (sens === 'BONUS' && a > effectiveBonusMax) a = effectiveBonusMax;
    setBonusMalusEdite(a === 0 ? null : (sens === 'MALUS' ? -a : a));
  };
  const handleMembrePersonnel = (checked, max) => {
    setMembrePersonnel(checked);
    if (max) setMaxPersonnel(max);
    if (checked) { setBonusMalusSens('BONUS'); setBonusMalusEdite(max || maxPersonnel); }
    else { setBonusMalusEdite(null); }
  };
  // Agence ciblée + taux (siège) à appliquer au contrat résultant de l'avenant
  const [avCom, setAvCom] = useState({ agence: null, taux: null, intermediaire: null });

  useEffect(() => {
    setModalProrogation(props.open);
    if (props.open && props?.contrat?.codeId) {
      console.log("Contrat pour prorogation:", props?.contrat);
      setContratActuel(props?.contrat);

      // Modification d'une PROPOSITION de prorogation : reprendre les valeurs déjà saisies
      // (sinon on re-prorogerait à partir de la nouvelle échéance).
      const isPropositionPR = props?.contrat?.avenant?.code === 'PR'
        && props?.contrat?.statutContrat === 'EN_PROPOSITION';
      if (isPropositionPR) {
        const effet = props?.contrat?.dateEffet ? dayjs(props.contrat.dateEffet) : null;
        const echeance = props?.contrat?.dateEcheance ? dayjs(props.contrat.dateEcheance) : null;
        if (effet) setDateEffetProrogation(effet);
        if (echeance) {
          setDateEcheanceProrogation(echeance);
          // Échéance = effet + durée − 1 jour → on rajoute 1 jour avant le diff pour retrouver
          // la durée entière (sinon le diff en mois est tronqué à durée − 1).
          if (effet) setDureeProrogation(echeance.add(1, 'day').diff(effet, 'month'));
        }
        // La dateEcheance du contrat est la NOUVELLE échéance : on récupère l'échéance ORIGINALE
        // (dernière version validée de la police) pour le plancher d'antidatage et les jours écoulés.
        const police = props?.contrat?.numeroPoliceP || props?.contrat?.numeroPoliceM;
        if (police) {
          axiosInstance.get(`/api/contrat/derniere-version-validee/${encodeURIComponent(police)}`)
            .then((res) => {
              const e = res?.data?.dateEcheance ? dayjs(res.data.dateEcheance) : null;
              setEcheanceOriginale(e);
              setJoursEcoules(e ? Math.max(0, dayjs().diff(e, 'day')) : 0);
            })
            .catch(() => { setEcheanceOriginale(null); setJoursEcoules(0); });
        } else {
          setEcheanceOriginale(null);
          setJoursEcoules(0);
        }
      } else {
        // Échéance ORIGINALE (contrat à proroger).
        const E = dayjs(props?.contrat?.dateEcheance);
        setEcheanceOriginale(E);
        // Défaut : AUJOURD'HUI si le contrat est déjà échu, sinon le LENDEMAIN de l'échéance
        // (continuité). = max(échéance+1, aujourd'hui).
        const aujourdhui = dayjs().startOf('day');
        const lendemainEcheance = E.add(1, 'day').startOf('day');
        const dateEffetDefaut = lendemainEcheance.isAfter(aujourdhui) ? lendemainEcheance : aujourdhui;
        setDateEffetProrogation(dateEffetDefaut);

        setDureeProrogation(6);
        // Échéance = date d'effet + durée − 1 jour (dernier jour couvert), comme partout ailleurs
        // (cf. ReportEffet). Gère le plafonnement de fin de mois (31/08 + 6M → 28/02, pas 27/02).
        setDateEcheanceProrogation(calculerEcheance(dateEffetDefaut, 6));

        // Jours écoulés depuis l'échéance originale.
        setJoursEcoules(Math.max(0, dayjs().diff(E, 'day')));
      }

      // Précharge les éditions manuelles si une proposition de prorogation est rouverte.
      const savedAccessoiresEdites = props?.contrat?.accessoiresEdites;
      setAccessoiresEdites(savedAccessoiresEdites != null ? savedAccessoiresEdites : null);
      const savedBonus = props?.contrat?.bonusMalusEdite;
      setBonusMalusEdite(savedBonus != null ? savedBonus : null);
      setBonusMalusSens(savedBonus != null && savedBonus < 0 ? 'MALUS' : 'BONUS');
      setMembrePersonnel(!!props?.contrat?.membrePersonnel);

      // Récupérer les frais accessoires pour la prorogation
      axiosInstance.get(`/api/avenant/accessoires/PR/${props?.contrat?.engins[0]?.categorie?.codeCategorie}`)
        .then(response => {
          setFraisAccessoires(response.data);
        })
        .catch(error => {
          console.error("Erreur lors de la récupération des frais accessoires:", error);
        });
    }
  }, [props.open, props?.contrat?.codeId]);

  const { CalendarOutlined, DollarOutlined, CheckCircleOutlined, WarningOutlined } = require('@ant-design/icons');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
  };

  const handleDateEffetChange = (date) => {
    setDateEffetProrogation(date);
    // Échéance = date d'effet + durée − 1 jour (dernier jour couvert).
    if (date && dureeProrogation) {
      setDateEcheanceProrogation(calculerEcheance(date, dureeProrogation));
    }
  };

  const handleDureeChange = (duree) => {
    setDureeProrogation(duree);
    if (dateEffetProrogation && duree) {
      setDateEcheanceProrogation(calculerEcheance(dateEffetProrogation, duree));
    }
  };

  const calculatePreview = async () => {
    if (!contratActuel || !dateEffetProrogation || !dureeProrogation) return;

    try {
      const payload = {
        contratCodeId: contratActuel.codeId,
        dateEffet: dateEffetProrogation.format('YYYY-MM-DD'),
        dureeEnMois: dureeProrogation,
        dateEcheance: dateEcheanceProrogation?.format('YYYY-MM-DD'),
        accessoiresEdites: (canEditAccessoires && accessoiresEdites != null && accessoiresEdites !== "")
          ? Number(accessoiresEdites) : null,
        bonusMalusEdite: ((canEditBonus || membrePersonnel) && bonusMalusEdite != null && bonusMalusEdite !== "")
          ? Number(bonusMalusEdite) : null,
        membrePersonnel: !!membrePersonnel,
      };

      const response = await axiosInstance.put('/api/contrat/preview-prorogation', JSON.stringify(payload));
      console.log("Preview prorogation:", response.data);
      setPreview(response.data);
    } catch (error) {
      console.error("Erreur lors du calcul du preview:", error);
    }
  };

  useEffect(() => {
    if (dateEffetProrogation && dureeProrogation && dateEcheanceProrogation) {
      calculatePreview();
    }
  }, [dateEffetProrogation, dureeProrogation, dateEcheanceProrogation, contratActuel]);

  const navigate = useNavigate();

  const enterLoading = () => setLoading(true);
  const outLoading = () => setLoading(false);

  const handleProrogationSubmit = async () => {
    try {
      await form.validateFields();

      // Vérifications d'éligibilité
      if (!isContratEligible()) {
        toast.error("Ce contrat n'est pas éligible à la prorogation");
        return;
      }

      const editionProposition = props?.contrat?.avenant?.code === "PR"
        && props?.contrat?.statutContrat === "EN_PROPOSITION";
      if (joursEcoules > 30 && !canAntidate && !editionProposition) {
        toast.error("La prorogation ne peut plus être effectuée (plus de 30 jours après expiration)");
        return;
      }

      enterLoading();

      const payload = {
        contratCodeId: contratActuel.codeId,
        dateEffet: dateEffetProrogation.format('YYYY-MM-DD'),
        dateEcheance: dateEcheanceProrogation.format('YYYY-MM-DD'),
        dureeEnMois: dureeProrogation,
        primeNette: preview?.primeNette || 0,
        primeNetteFractionnee: preview?.primeNetteFractionnee || 0,
        accessoires: (canEditAccessoires && accessoiresEdites != null && accessoiresEdites !== "")
          ? Number(accessoiresEdites) : fraisAccessoires,
        accessoiresEdites: (canEditAccessoires && accessoiresEdites != null && accessoiresEdites !== "")
          ? Number(accessoiresEdites) : null,
        bonusMalusEdite: ((canEditBonus || membrePersonnel) && bonusMalusEdite != null && bonusMalusEdite !== "")
          ? Number(bonusMalusEdite) : null,
        membrePersonnel: !!membrePersonnel,
        taxes: preview?.taxes || 0,
        primeTotale: preview?.primeTotale || 0,
        joursEcoules
      };

      console.log("Payload prorogation:", payload);

      // Édition d'une proposition existante (PR + EN_PROPOSITION) → mise à jour EN PLACE
      const isPropositionEdit = props?.contrat?.avenant?.code === "PR"
        && props?.contrat?.statutContrat === "EN_PROPOSITION";
      const endpoint = isPropositionEdit
        ? `/api/contrat/prorogation/proposition`
        : `/api/contrat/prorogation`;

      const response = await axiosInstance.put(endpoint, JSON.stringify(payload));

      await applyAvenantAgenceCommission(response.data?.codeId, avCom.agence, avCom.taux, avCom.intermediaire);
      toast.success(isPropositionEdit
        ? "Proposition de prorogation mise à jour."
        : "Prorogation enregistrée en proposition.");
      setModalProrogation(false);
      props.update(false);
      navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
      window.location.reload();

    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la prorogation du contrat");
    } finally {
      outLoading();
    }
  };

  const handleCancel = () => {
    setModalProrogation(false);
    props.update(false);
  };

  const isContratEligible = () => {
    // Édition d'une proposition PR déjà créée : l'éligibilité a été vérifiée à la création →
    // on ne re-bloque pas sur la limite des 30 jours (sinon impossible de rééditer la date).
    const editionProposition = contratActuel?.avenant?.code === 'PR'
      && contratActuel?.statutContrat === 'EN_PROPOSITION';
    return contratActuel?.fractionnement?.nombre > 1 &&
      contratActuel?.avenant?.code !== 'SP' &&
      (joursEcoules <= 30 || canAntidate || editionProposition); // antidatage (HCO081) : ignore la limite de 30 jours
  };

  const getDureeRestanteMaximale = () => {
    // La durée cumulée ne peut pas excéder 12 mois
    const dureeActuelle = contratActuel?.dureeEnMois || 6;
    return 12 - dureeActuelle;
  };

  return (
    <SDIModal
      title="PROROGATION DE CONTRAT"
      centered
      confirmLoading={loading}
      open={modalProrogation}
      onOk={handleProrogationSubmit}
      onCancel={handleCancel}
      width={1000}
      zIndex={1940}
      okText="Établir la prorogation"
      cancelText="Annuler"
    >
      <ConfigProvider locale={frFR}>
        <div className="prorogation-content">
          <AvenantAgencySelector
            organismeCode={props?.contrat?.organisme?.code}
            defaultAgence={props?.contrat?.courtier || props?.contrat?.producteur?.agence || props?.contrat?.intermediaire?.agence}
            defaultIntermediaire={props?.contrat?.intermediaire}
            categorie={props?.contrat?.engins?.[0]?.categorie?.codeCategorie}
            onChange={(ag, t, inter) => setAvCom({ agence: ag, taux: t, intermediaire: inter })}
          />

          {/* Informations sur le contrat */}
          {contratActuel && (
            <Card className="mb-4" size="small" title="Informations du contrat à proroger">
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="Numéro de police"
                    value={contratActuel.numeroPoliceP || contratActuel.numeroPoliceM}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Fractionnement"
                    value={`${contratActuel.fractionnement?.moisMin || 'N/A'} mois`}
                    valueStyle={{ color: (contratActuel.fractionnement?.nombre > 1) ? '#3f8600' : '#cf1322' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Échéance actuelle"
                    value={dayjs(contratActuel.dateEcheance).format('DD/MM/YYYY')}
                    prefix={<CalendarOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Jours écoulés"
                    value={`${joursEcoules} jours`}
                    valueStyle={{ color: joursEcoules <= 30 ? '#3f8600' : '#cf1322' }}
                  />
                </Col>
              </Row>
            </Card>
          )}

          {/* Conditions d'éligibilité */}
          {!isContratEligible() ? (
            <Alert
              message="Contrat non éligible à la prorogation"
              description={
                contratActuel?.fractionnement?.nombre <= 1
                  ? "La prorogation n'est autorisée que pour les contrats fractionnés."
                  : joursEcoules > 30
                    ? "La prorogation ne peut être effectuée que dans les 30 jours suivant l'expiration."
                    : contratActuel?.avenant?.code === 'SP'
                      ? "Un contrat suspendu ne peut pas être prorogé."
                      : "Le contrat doit justifier d'un avenant de renouvellement de 6 mois en cours ou expiré depuis maximum 30 jours."
              }
              type="error"
              icon={<WarningOutlined />}
              showIcon
              className="mb-4"
            />
          ) : (
            <Alert
              message="Contrat éligible à la prorogation"
              description="Ce contrat remplit les conditions pour être prorogé (deuxième période de 6 mois)."
              type="success"
              icon={<CheckCircleOutlined />}
              showIcon
              className="mb-4"
            />
          )}

          {/* Informations sur la prorogation */}
          {/* <Card className="mb-4" size="small" title="Règles de prorogation">
            <div className="info-section">
              <div className="info-item">
                <InfoCircleOutlined className="info-icon info" />
                <Text>
                  <Text strong>Code avenant :</Text> PR2024026 (renouvellement du deuxième six mois)
                </Text>
              </div>
              <div className="info-item">
                <ClockCircleOutlined className="info-icon warning" />
                <Text>
                  <Text strong>Durée maximale :</Text> La durée cumulée ne peut excéder 12 mois
                </Text>
              </div>
              <div className="info-item">
                <CalendarOutlined className="info-icon success" />
                <Text>
                  <Text strong>Date d'effet :</Text> Par défaut le jour suivant l'expiration,
                  ou dans les 30 jours suivants
                </Text>
              </div>
            </div>
          </Card> */}

          <Form form={form} layout="vertical">
            {/* Date d'effet de la prorogation */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Date d'effet de la prorogation"
                  name="dateEffetProrogation"
                  rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                  initialValue={dateEffetProrogation}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    value={dateEffetProrogation}
                    onChange={handleDateEffetChange}
                    disabledDate={(current) => {
                      if (!current) return false;
                      const today = dayjs().startOf('day');
                      // Lendemain de l'échéance ORIGINALE (E+1) : borne d'antidatage.
                      const lendemain = echeanceOriginale
                        ? echeanceOriginale.add(1, 'day').startOf('day')
                        : today;
                      // Plancher : avec antidatage (HCO081) → jusqu'à E+1 (lendemain de l'échéance
                      // passée) ; sans antidatage → pas de recul, min = max(E+1, aujourd'hui) (donc
                      // aujourd'hui si le contrat est déjà échu, E+1 s'il ne l'est pas encore).
                      const floor = canAntidate
                        ? lendemain
                        : (lendemain.isAfter(today) ? lendemain : today);
                      return current.isBefore(floor, 'day');
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Durée de prorogation"
                  name="dureeProrogation"
                  rules={[{ required: true, message: 'Veuillez sélectionner une durée' }]}
                  initialValue={dureeProrogation}
                >
                  <Select
                    value={dureeProrogation}
                    onChange={handleDureeChange}
                    style={{ width: '100%' }}
                  >
                    <Option value={6}>6 mois (deuxième période standard)</Option>
                    {getDureeRestanteMaximale() > 6 && (
                      <>
                        <Option value={3}>3 mois</Option>
                        <Option value={4}>4 mois</Option>
                        <Option value={5}>5 mois</Option>
                      </>
                    )}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Informations calculées */}
            {dateEffetProrogation && dateEcheanceProrogation && (
              <Card className="mb-4" size="small" title="Informations calculées">
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Date d'échéance de la prorogation"
                      value={dateEcheanceProrogation.format('DD/MM/YYYY')}
                      valueStyle={{ color: '#1890ff' }}
                      prefix={<CalendarOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Durée totale cumulée"
                      value={`${(contratActuel?.fractionnement?.moisMin || 6) + dureeProrogation} mois`}
                      valueStyle={{
                        color: ((contratActuel?.fractionnement?.moisMin || 6) + dureeProrogation) <= 12 ? '#3f8600' : '#cf1322'
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Type d'avenant"
                      value={`PR${new Date().getFullYear()}026`}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                </Row>
              </Card>
            )}

            {/* Éditions manuelles : frais accessoires (HCO079) et bonus/malus (HCO080) */}
            {(canEditAccessoires || canEditBonus) && isContratEligible() && (
              <Card size="small" title="Ajustements" style={{ marginBottom: 12 }}>
                <Row gutter={[12, 12]} align="bottom">
                  {canEditAccessoires && (
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                        Frais accessoires (défaut : {formatCurrency(fraisAccessoires)})
                      </Text>
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        value={accessoiresEdites != null ? accessoiresEdites : (preview?.accessoires ?? fraisAccessoires)}
                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                        parser={(v) => v.replace(/\s/g, "")}
                        onChange={(v) => setAccessoiresEdites(v)}
                        onPressEnter={() => calculatePreview()}
                      />
                    </Col>
                  )}
                  {canMarkPersonnel && (
                    <Col span={24} style={{ marginBottom: 8 }}>
                      <MembrePersonnelCheck checked={membrePersonnel} onToggle={handleMembrePersonnel} />
                    </Col>
                  )}
                  {(canEditBonus || membrePersonnel) && (
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                        Bonus / Malus (Bonus = réduction, max {effectiveBonusMax} %)
                      </Text>
                      <Space.Compact style={{ width: "100%" }}>
                        <Segmented
                          value={bonusMalusSens}
                          onChange={(sens) => applyBonusSens(sens, bonusMalusAbs)}
                          options={[
                            { label: "Bonus", value: "BONUS" },
                            { label: "Malus", value: "MALUS" },
                          ]}
                        />
                        <InputNumber
                          style={{ flex: 1 }}
                          min={0}
                          max={bonusMalusSens === "BONUS" ? effectiveBonusMax : 100}
                          step={1}
                          addonAfter="%"
                          value={bonusMalusAbs}
                          placeholder="0"
                          onChange={(v) => applyBonusSens(bonusMalusSens, v)}
                          onPressEnter={() => calculatePreview()}
                        />
                      </Space.Compact>
                    </Col>
                  )}
                  <Col span={24} style={{ textAlign: "right" }}>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={() => calculatePreview()}>
                      Recalculer l'aperçu
                    </Button>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Récapitulatif financier */}
            {preview && (
              <Card className="cost-summary" size="small">
                <Title level={5}>
                  <DollarOutlined className="mr-2" />
                  Récapitulatif
                </Title>
                <div className="cost-breakdown">
                  <Row justify="space-between" className="cost-line">
                    <Col>
                      <Text>Prime nette annuelle (avec Bonus-Malus et réductions):</Text>
                    </Col>
                    <Col>
                      <Text strong>{formatCurrency(preview.primeNette || 0)}</Text>
                    </Col>
                  </Row>

                  <Row justify="space-between" className="cost-line">
                    <Col>
                      <Text>Frais accessoires:</Text>
                    </Col>
                    <Col>
                      <Text>{formatCurrency(preview?.accessoires != null ? preview.accessoires : fraisAccessoires)}</Text>
                    </Col>
                  </Row>

                  <Row justify="space-between" className="cost-line">
                    <Col>
                      <Text>Prime nette fractionnée (Prime nette annuelle + accessoires):</Text>
                    </Col>
                    <Col>
                      <Text strong>{formatCurrency(
                        (preview.primeNetteFractionnee || 0) +
                        (preview.accessoiresFractionnee != null
                          ? preview.accessoiresFractionnee
                          : (preview.accessoires || 0) * (preview.fractionnement || 0) / 100)
                      )}</Text>
                    </Col>
                  </Row>


                  <Row justify="space-between" className="cost-line">
                    <Col>
                      <Text>Taxes (6%):</Text>
                    </Col>
                    <Col>
                      <Text>{formatCurrency(preview.taxes || 0)}</Text>
                    </Col>
                  </Row>

                  <Divider className="my-2" />

                  <Row justify="space-between" className="cost-line total-cost">
                    <Col>
                      <Text strong className="text-lg">PRIME TOTALE:</Text>
                    </Col>
                    <Col>
                      <Text strong className="text-lg text-blue-600">
                        {formatCurrency(preview.primeTotale || 0)}
                      </Text>
                    </Col>
                  </Row>
                </div>
              </Card>
            )}
          </Form>
        </div>
      </ConfigProvider>

      <style jsx>
        {`
                    .prorogation-content {
                        max-height: 70vh;
                        overflow-y: auto;
                    }
                    
                    .info-section {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    
                    .info-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    
                    .info-icon {
                        margin-top: 2px;
                        font-size: 16px;
                    }
                    
                    .info-icon.warning {
                        color: #faad14;
                    }
                    
                    .info-icon.info {
                        color: #1890ff;
                    }
                    
                    .info-icon.success {
                        color: #52c41a;
                    }
                    
                    .cost-summary {
                        background-color: #f8f9fa;
                        border: 1px solid #e9ecef;
                    }
                    
                    .cost-breakdown .cost-line {
                        margin-bottom: 8px;
                    }
                    
                    .cost-breakdown .total-cost {
                        background-color: #e6f7ff;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    
                    .mr-2 { margin-right: 8px; }
                    .mb-4 { margin-bottom: 16px; }
                    .my-2 { margin: 8px 0; }
                    .text-lg { font-size: 16px; }
                    .text-blue-600 { color: #1890ff; }
                `}
      </style>
    </SDIModal>
  );
};

Prorogation.propTypes = {
  open: PropTypes.bool,
  update: PropTypes.func,
  contrat: PropTypes.object
};

export default Prorogation;
